import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Input, SearchableSelect, useCart, useToast, v } from '@kss/ui';
import { useAuth } from '@kss/auth';
import {
  FREE_SHIPPING,
  invoiceService,
  paymentService,
  salesOrderService,
  shippingFor,
  storeService,
  warehouseService,
  type PublicShipping,
  type SalesOrderCreate,
  type Invoice,
  type InvoiceItem,
  type SalesOrderItemCreate,
} from '@kss/api';
import {
  Banknote,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Landmark,
  Loader2,
  MapPin,
  Plus,
  QrCode,
  Truck,
  Wallet,
} from 'lucide-react';
import { lookupPincode } from '../utils/pincodeLookup';
import { COUNTRIES, INDIAN_CITIES, INDIAN_STATES } from '../utils/indiaLocations';

/**
 * Public-web checkout flow.
 *
 * The backend (verified 2026-05-07) now persists the SalesOrder header
 * AND its nested `items[]` in one transaction, and returns the created
 * entity from the create call. The previous five-call dance has
 * collapsed to three:
 *
 *   1. POST /api/SalesOrder        — header + nested items[] in one go
 *      (legacy fallback: filter by orderNumber if the response is the
 *      old `data: true` shape)
 *   2. POST /api/Invoice           — header + nested invoiceItems[]
 *   3. POST /api/Payment           — payment row tied to the invoice
 *                                    (skipped on COD)
 *
 * Every call requires Bearer auth, so the page redirects to the login
 * modal if the shopper isn't signed in. After success we land on
 * `/account/orders/:id` so the customer immediately sees their order.
 */

interface AddressForm {
  fullName: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  pin: string;
  country: string;
}

const EMPTY_ADDRESS: AddressForm = {
  fullName: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  pin: '',
  country: 'India',
};

/**
 * Wire-compatible shape of a row in the customer's address book —
 * mirrors the schema used by `apps/web/src/account/AccountAddresses.tsx`,
 * which persists to `localStorage` under `kss.addresses.v1:<userId>`.
 *
 * Once the backend ships `customerAddressService` (see
 * BACKEND_AGENT_PROMPTS Item 3) this read becomes a service call; the
 * UI shape stays the same.
 */
interface SavedAddress {
  id: string;
  label: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  isDefault?: boolean;
}

function readSavedAddresses(userId?: string | null): SavedAddress[] {
  try {
    const raw = localStorage.getItem(`kss.addresses.v1:${userId ?? 'guest'}`);
    if (raw) return JSON.parse(raw) as SavedAddress[];
  } catch {
    /* malformed JSON — start clean */
  }
  return [];
}

type PayMode = 'cod' | 'upi' | 'card' | 'netbanking' | 'wallet';

// PaymentMode enum on the live API: 1=Cash, 2=Card, 3=UPI, 4=NetBanking,
// 5=Cheque, 6=Wallet, 7=Other (mapped from a probe of the Payment seed).
const PAYMENT_MODE_INT: Record<PayMode, number> = {
  cod: 1, // cash on delivery uses Cash mode internally
  upi: 3,
  card: 2,
  netbanking: 4,
  wallet: 6,
};

/* ----------------------------------------------------------------- */
/* GST                                                                */
/* ----------------------------------------------------------------- */

/**
 * Rate applied when a product carries no `taxRate` of its own.
 *
 * 18% is the most common Indian GST slab and matches what this page
 * previously charged on EVERY line. Keeping it as the fallback means a
 * product with no rate configured is invoiced exactly as before; the
 * change is that a product WITH a rate is now billed at that rate
 * instead of having it overwritten.
 */
const DEFAULT_TAX_RATE = 18;

/** Round to paise. Currency maths must never carry float dust into a total. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The GST rate to bill a cart line at.
 *
 * `taxRate` is a real column on the product, projected onto the public
 * card DTO and already shown to the shopper as a "Tax: 5%" spec on the
 * product page. Reading it here is what stops the invoice contradicting
 * that spec. Zero is a legitimate rate (exempt goods) and is honoured;
 * only a missing or malformed value falls back.
 */
function lineTaxRate(product: { taxRate?: number }): number {
  const r = Number(product.taxRate);
  return Number.isFinite(r) && r >= 0 ? r : DEFAULT_TAX_RATE;
}

/**
 * Per-line tax breakdown, rounded once at the line level.
 *
 * `cgst + sgst === tax` exactly, by construction: the halves are derived
 * by rounding one and subtracting, never by rounding both. Rounding both
 * independently drifts by a paise on any odd rate (a 5% line on ₹100.10
 * gives 2.5025 each → 2.50 + 2.50 = 5.00 vs a 5.01 line tax), and
 * `InvoiceBalanceValidator` rejects the invoice server-side when
 * Σ items ≠ header.
 */
function lineTax(product: { taxRate?: number }, taxableAmount: number) {
  const rate = lineTaxRate(product);
  const tax = round2(taxableAmount * (rate / 100));
  const cgst = round2(tax / 2);
  return { rate, tax, cgst, sgst: round2(tax - cgst) };
}

// Payment-method catalog rendered as a tile grid in the checkout
// "Payment method" section. Icon is the visual cue; `caption` is a
// one-line subtitle so the tile reads like a card brand selector.
const PAYMENT_METHODS: Array<{
  id: PayMode;
  label: string;
  caption: string;
  Icon: typeof Truck;
}> = [
  { id: 'cod', label: 'Cash on delivery', caption: 'Pay when your order arrives', Icon: Truck },
  { id: 'upi', label: 'UPI', caption: 'GPay · PhonePe · Paytm · BHIM', Icon: QrCode },
  { id: 'card', label: 'Credit / Debit card', caption: 'Visa · Mastercard · RuPay', Icon: CreditCard },
  { id: 'netbanking', label: 'Net banking', caption: 'All major Indian banks', Icon: Landmark },
  { id: 'wallet', label: 'Wallet', caption: 'Paytm · Mobikwik · Amazon Pay', Icon: Wallet },
];

export default function CheckoutPage() {
  const cart = useCart();
  const toast = useToast();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [address, setAddress] = useState<AddressForm>(EMPTY_ADDRESS);
  // Saved-address picker. Read once on mount from the same
  // `kss.addresses.v1:<userId>` slot the account-addresses page
  // writes to. Tracks which row is currently applied so the chip
  // can highlight; an empty `selectedAddrId` means "I'm typing a
  // fresh address by hand" (the default for new customers).
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [selectedAddrId, setSelectedAddrId] = useState<string>('');
  const [payMode, setPayMode] = useState<PayMode>('cod');
  const [submitting, setSubmitting] = useState(false);
  const [storeId, setStoreId] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  /* Delivery terms for the resolved store. FREE_SHIPPING until the
     bootstrap resolves — never quote a fee that hasn't been read. */
  const [shippingTerms, setShippingTerms] = useState<PublicShipping>(FREE_SHIPPING);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  // PIN-resolver loading state — shown next to the PIN field while
  // the api.postalpincode.in lookup is in flight.
  const [resolvingPin, setResolvingPin] = useState(false);
  const lastResolvedPin = useRef('');
  // Collapsible sections — both open by default so a first-time
  // shopper sees everything they need; clicking the header collapses
  // either one independently. Mirrors the accordion pattern on the
  // product detail page.
  const [addressOpen, setAddressOpen] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(true);

  // Redirect logged-out users to the login modal — checkout requires
  // a real customer JWT for the SalesOrder POST. The modal carries the
  // user back to `/checkout` on successful sign-in.
  useEffect(() => {
    if (!isAuthenticated) {
      window.dispatchEvent(
        new CustomEvent('kss:open-login', { detail: { redirect: '/checkout' } }),
      );
    }
  }, [isAuthenticated]);

  // Bootstrap the storeId + warehouseId. The SalesOrder POST requires
  // both, but the storefront has no UI to pick them — fetch the first
  // active row of each on mount and use as the default. (Backend
  // recommendation: default these from the JWT tenant when omitted —
  // see BACKEND_AGENT_PROMPTS Item 4.)
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const [stores, warehouses] = await Promise.all([
          storeService.all(),
          warehouseService.all(),
        ]);
        if (cancelled) return;
        /* Ordered by code, matching `GetShippingAsync` server-side.
           Both used to take "whichever active row came back first" from
           an unordered list, so the cart could quote one store's fee and
           the checkout charge another's. */
        const firstStore =
          (stores ?? [])
            .filter((s) => s.isActive !== false)
            .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''))[0] ?? stores?.[0];
        const firstWh = (warehouses ?? []).find((w) => w.isActive !== false) ?? warehouses?.[0];
        if (!firstStore?.storeId) throw new Error('No active store on this tenant');
        if (!firstWh?.warehouseId) throw new Error('No active warehouse on this tenant');
        setStoreId(firstStore.storeId);
        setWarehouseId(firstWh.warehouseId);
        /* Fee comes off the store the order is actually raised against,
           not the anonymous /Public/shipping lookup — same row, but this
           way the charge and the SalesOrder.storeId can never disagree. */
        setShippingTerms({
          shippingCharge: Number(firstStore.shippingCharge) || 0,
          freeShippingThreshold:
            firstStore.freeShippingThreshold == null
              ? null
              : Number(firstStore.freeShippingThreshold),
        });
      } catch (e) {
        if (cancelled) return;
        setBootstrapError(e instanceof Error ? e.message : 'Could not load store / warehouse');
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (cart.items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <h1 className="text-xl font-bold">Cart is empty</h1>
        <p className="mt-2 text-sm text-content-secondary">
          Add a product before going to checkout.
        </p>
        <Button className="mt-4" onClick={() => navigate('/shop')}>
          Browse products
        </Button>
      </div>
    );
  }

  const shipping = shippingFor(cart.subtotal, shippingTerms);
  /* GST summed per line at each product's own rate.

     This used to be a flat 18% on the subtotal, defended by a comment
     claiming "the backend recomputes the exact split when we POST the
     Invoice". It does not: `InvoiceService` never writes CGST/SGST, and
     `InvoiceBalanceValidator` only checks that the numbers the client
     sent add up among themselves. Whatever this computes IS the tax
     document, so it has to be right here. */
  /** Σ of the per-line taxable amounts — the base every total builds on. */
  const taxableTotal = round2(
    cart.items.reduce((s, line) => s + round2(line.product.price * line.qty), 0),
  );
  const taxAmount = round2(
    cart.items.reduce(
      (sum, line) => sum + lineTax(line.product, round2(line.product.price * line.qty)).tax,
      0,
    ),
  );
  const total = round2(taxableTotal + shipping + taxAmount);
  /** Single GST rate to quote in the summary, or `null` for a mixed cart. */
  const gstRateLabel = (() => {
    const rates = new Set(cart.items.map((line) => lineTaxRate(line.product)));
    return rates.size === 1 ? [...rates][0] : null;
  })();

  /**
   * Per-field errors for the shipping address.
   *
   * Replaces a presence-and-length check that accepted a 10-character
   * phone of any digits (`0000000000`), a 6-character pin containing
   * letters, and any markup at all in the name — values that then went
   * onto the sales order, the invoice, and the printed shipping block.
   *
   * Text fields carry the SQL/XSS guards for the same reason as
   * AccountAddresses: these strings are rendered into the print
   * document, outside React's escaping.
   */
  const addressErrors: Record<string, string | null> = {
    fullName: v.runRules(address.fullName, [
      v.required('Full name'),
      v.maxLength(120, 'Full name'),
      ...v.safeText('Full name'),
    ]),
    phone: v.runRules(address.phone, [v.required('Phone'), v.mobile({ label: 'Phone' })]),
    line1: v.runRules(address.line1, [
      v.required('Address line 1'),
      v.maxLength(200, 'Address line 1'),
      ...v.safeText('Address line 1'),
    ]),
    line2: v.runRules(address.line2 ?? '', [
      v.maxLength(200, 'Address line 2'),
      ...v.safeText('Address line 2'),
    ]),
    city: v.runRules(address.city, [v.required('City'), v.maxLength(100, 'City'), ...v.safeText('City')]),
    state: v.runRules(address.state, [v.required('State'), v.maxLength(100, 'State'), ...v.safeText('State')]),
    pin: v.runRules(address.pin, [
      v.required('PIN code'),
      v.numericOnly('PIN code'),
      v.minLength(6, 'PIN code'),
      v.maxLength(6, 'PIN code'),
    ]),
  };
  const [addrTouched, setAddrTouched] = useState(false);
  const addrErr = (k: string) => (addrTouched ? addressErrors[k] ?? undefined : undefined);
  const markAddr = () => setAddrTouched(true);
  const valid = !Object.values(addressErrors).some(Boolean);

  const set = <K extends keyof AddressForm>(k: K, v: AddressForm[K]) =>
    setAddress((p) => ({ ...p, [k]: v }));

  /**
   * Map a saved-address row to the checkout's AddressForm shape.
   * The schemas almost line up; the only translations are
   * pincode→pin and pulling `fullName` off the signed-in user's
   * profile (the address book doesn't store a per-row name yet).
   */
  function applySaved(addr: SavedAddress) {
    setSelectedAddrId(addr.id);
    setAddress({
      fullName: user?.name ?? '',
      phone: addr.phone || '',
      line1: addr.line1 || '',
      line2: addr.line2 || '',
      city: addr.city || '',
      state: addr.state || '',
      pin: addr.pincode || '',
      // Address book is India-only today (storage is per-user, no
      // country picker on the form yet) — `91` is the ITU-T calling
      // code we use everywhere else, mirroring the form default.
      country: 'India',
    });
  }

  // Load saved addresses on mount / when user switches; auto-apply
  // the row marked default so a returning customer doesn't have to
  // tap before their address is filled in.
  useEffect(() => {
    if (!isAuthenticated) return;
    const list = readSavedAddresses(user?.id);
    setSavedAddresses(list);
    const def = list.find((a) => a.isDefault) ?? list[0];
    if (def) {
      applySaved(def);
    }
    // The applySaved closure pulls user.name; explicitly listing
    // user?.id covers the sign-out → sign-in account swap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  /**
   * Auto-fill city / state / country whenever the user types a valid
   * 6-digit Indian PIN. Debounced ~500ms. Cached via the lookup
   * helper. Fails closed — if the API is down or the PIN is invalid,
   * the city / state inputs are left untouched.
   */
  useEffect(() => {
    const pin = (address.pin ?? '').trim();
    if (!/^\d{6}$/.test(pin)) return;
    if (lastResolvedPin.current === pin) return;
    const timer = setTimeout(async () => {
      setResolvingPin(true);
      try {
        const res = await lookupPincode(pin);
        if (!res) {
          toast.info(
            'PIN not found',
            'Could not auto-fill city / state — please pick manually.',
          );
          return;
        }
        lastResolvedPin.current = pin;
        setAddress((p) => ({
          ...p,
          city: res.city || p.city,
          state: res.state || p.state,
          country: res.country || p.country || 'India',
        }));
        toast.success('Address auto-filled', `${res.city}, ${res.state}`);
      } finally {
        setResolvingPin(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // toast is stable from the provider; the resolved-pin ref dedups.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.pin]);

  async function placeOrder() {
    if (!valid || !user?.id || !storeId || !warehouseId) return;
    setSubmitting(true);
    try {
      const orderNumber = `WEB-${Date.now()}`;
      const shippingAddressJson = JSON.stringify(address);
      /* `taxableTotal`, not `round2(cart.subtotal)`.
         `InvoiceBalanceValidator` requires
           totalAmount − shipping === Σ items.total
         and each `items[].total` is built from its own rounded taxable
         amount, so the header must be summed the same way. Rounding the
         cart total once and rounding each line separately agree only
         while every price is exactly 2dp — true today, but a silent
         server-side rejection the first time it isn't. */
      const subTotal = taxableTotal;
      const totalAmount = round2(total);

      // 1. Create the SalesOrder with nested items[]. The live API
      //    (verified 2026-05-07) now returns the created entity AND
      //    persists nested items in one transaction, so we can read
      //    the GUID off the response and skip the legacy duplicate
      //    POST /SalesOrderItem fan-out.
      const itemsBody: SalesOrderItemCreate[] = cart.items.map((line) => {
        const lineSubtotal = round2(line.product.price * line.qty);
        const { rate, tax } = lineTax(line.product, lineSubtotal);
        return {
          // Placeholder GUID — backend overwrites with the new SO id.
          salesOrderId: '00000000-0000-0000-0000-000000000000',
          storeId,
          productId: String(line.product.id),
          quantity: line.qty,
          unitPrice: line.product.price,
          discountAmount: 0,
          taxRate: rate,
          taxAmount: tax,
          total: round2(lineSubtotal + tax),
        };
      });
      const orderBody: SalesOrderCreate & { items: SalesOrderItemCreate[] } = {
        storeId,
        customerId: user.id,
        warehouseId,
        orderNumber,
        orderDate: new Date().toISOString(),
        channel: 3, // Online
        status: 1, // Pending
        paymentStatus: payMode === 'cod' ? 1 : 2, // Pending vs Paid
        subTotal,
        discountAmount: 0,
        taxAmount,
        shippingAmount: shipping,
        totalAmount,
        shippingAddressJson,
        notes: `Web checkout · pay=${payMode}`,
        /* `isActive` removed — strict-mode binder rejects it with
           "Unknown field 'isActive' in request body" (verified live
           2026-05-14). Active-ness is implicit. */
        items: itemsBody,
      };

      // Try to read the new id off the create response. Some older
      // backend builds return `data: true` instead of the entity —
      // in that case we filter by the (uniquely-generated)
      // orderNumber to recover the GUID.
      let salesOrderId: string | undefined;
      try {
        const created = await salesOrderService.create([
          orderBody as unknown as SalesOrderCreate,
        ]);
        salesOrderId = (created ?? [])[0]?.salesOrderId;
      } catch (e) {
        // Surface stock errors clearly so the user knows why the
        // order didn't go through.
        const msg = (e instanceof Error && e.message) || 'Order save failed';
        if (/stock|on-hand/i.test(msg)) {
          toast.error(
            'Out of stock',
            'One or more items are out of stock — please remove them and try again.',
          );
          setSubmitting(false);
          return;
        }
        throw e;
      }
      if (!salesOrderId) {
        const matches = await salesOrderService.filter({ orderNumber });
        salesOrderId = (matches ?? []).find((o) => o.orderNumber === orderNumber)?.salesOrderId;
      }
      if (!salesOrderId) {
        throw new Error('Order saved, but the server did not return its id.');
      }

      // 4. Issue an Invoice (header + nested invoiceItems[]). The
      //    Invoice endpoint DOES honour the nested array, so the
      //    line-items contract here is different from SalesOrder.
      const invoiceItems: Omit<InvoiceItem, 'invoiceItemId' | 'tenantId'>[] = cart.items.map(
        (line) => {
          const taxableAmount = round2(line.product.price * line.qty);
          /* Split at the product's own rate — a 5% item bills 2.5% CGST
             + 2.5% SGST, not the flat 9 + 9 this used to hardcode. The
             rates carried on the row are what the printed invoice shows
             in its GST columns, so they have to match the amounts. */
          const { rate, tax, cgst, sgst } = lineTax(line.product, taxableAmount);
          const halfRate = rate / 2;
          return {
            invoiceId: '00000000-0000-0000-0000-000000000000',
            storeId,
            productId: String(line.product.id),
            itemName: line.product.title,
            quantity: line.qty,
            unitPrice: line.product.price,
            discountAmount: 0,
            taxableAmount,
            cgstRate: halfRate,
            sgstRate: halfRate,
            igstRate: 0,
            cgstAmount: cgst,
            sgstAmount: sgst,
            igstAmount: 0,
            total: round2(taxableAmount + tax),
          };
        },
      );
      /* Line-item aggregates. Shipping is NOT a tax-bearing invoice
         line, so it stays out of `cgst / sgst / igst` and out of
         Σ(items[].total) — but it IS part of the invoice grand total
         and belongs in the dedicated `shippingAmount` header field.

         History: the 2026-05-26 workaround dropped shipping entirely
         to get past "Invoice does not balance: TotalAmount mismatch:
         computed=234.82 header=333.82". That predated the F2 change
         which taught InvoiceBalanceValidator about ShippingAmount:
           expected      = subTotal + cgst + sgst + igst
                           − discount + roundOff + shippingAmount
           expectedLines = totalAmount − shippingAmount
         Because the workaround also omitted `shippingAmount`, every
         web invoice under-reported by exactly the delivery fee — the
         ₹1,041 order that printed as ₹942. Setting both fields
         satisfies the validator AND matches the sales order. */
      const invoiceCgst = Number(
        invoiceItems.reduce((s, it) => s + Number(it.cgstAmount ?? 0), 0).toFixed(2),
      );
      const invoiceSgst = Number(
        invoiceItems.reduce((s, it) => s + Number(it.sgstAmount ?? 0), 0).toFixed(2),
      );
      const invoiceIgst = Number(
        invoiceItems.reduce((s, it) => s + Number(it.igstAmount ?? 0), 0).toFixed(2),
      );
      /** Σ of the taxable line rows — must equal `totalAmount − shippingAmount`. */
      const invoiceLineTotal = Number(
        invoiceItems.reduce((s, it) => s + Number(it.total ?? 0), 0).toFixed(2),
      );
      /** Invoice grand total — line rows plus the non-taxable delivery fee. */
      const invoiceTotal = Number((invoiceLineTotal + shipping).toFixed(2));
      const invoiceBody: Omit<Invoice, 'invoiceId' | 'tenantId'> & { items: typeof invoiceItems } = {
        salesOrderId,
        storeId,
        customerId: user.id,
        invoiceNumber: `INV-${Date.now()}`,
        invoiceDate: new Date().toISOString(),
        gstType: 1, // Intra-state CGST + SGST
        subTotal,
        discountAmount: 0,
        cgstAmount: invoiceCgst,
        sgstAmount: invoiceSgst,
        igstAmount: invoiceIgst,
        roundOff: 0,
        shippingAmount: shipping,
        totalAmount: invoiceTotal,
        paidAmount: payMode === 'cod' ? 0 : invoiceTotal,
        balanceAmount: payMode === 'cod' ? invoiceTotal : 0,
        status: payMode === 'cod' ? 1 : 3, // Pending vs Paid
        notes: `Auto-issued at checkout (pay=${payMode})`,
        items: invoiceItems,
      };
      let createdInvoiceId: string | undefined;
      try {
        await invoiceService.create([invoiceBody as unknown as Invoice]);
        // Recover the new invoiceId by filtering on invoiceNumber.
        const inv = await invoiceService.filter({ invoiceNumber: invoiceBody.invoiceNumber });
        createdInvoiceId = (inv ?? []).find(
          (r) => r.invoiceNumber === invoiceBody.invoiceNumber,
        )?.invoiceId;
      } catch (e) {
        // Non-fatal — order + items are saved; merchant can issue the
        // invoice manually from the panel if this 500s on stock /
        // tax-config issues.
        const msg = e instanceof Error ? e.message : 'Invoice failed';
        toast.error('Invoice not generated', msg);
      }

      // 5. Record the Payment row (only when the customer chose a
      //    paid mode upfront — COD is collected on delivery, so no
      //    Payment row yet).
      if (createdInvoiceId && payMode !== 'cod') {
        try {
          await paymentService.create([
            {
              invoiceId: createdInvoiceId,
              storeId,
              customerId: user.id,
              paymentNumber: `PAY-${Date.now()}`,
              paymentDate: new Date().toISOString(),
              mode: PAYMENT_MODE_INT[payMode],
              amount: totalAmount,
              referenceNumber: `WEB-${payMode.toUpperCase()}-${Date.now()}`,
              notes: 'Captured at web checkout',
            },
          ]);
        } catch (e) {
          // Non-fatal.
          const msg = e instanceof Error ? e.message : 'Payment failed';
          toast.error('Payment not recorded', msg);
        }
      }

      cart.clear();
      toast.success('Order placed', `${orderNumber} · ₹${totalAmount.toLocaleString('en-IN')}`);
      navigate(`/account/orders/${salesOrderId}`);
    } catch (e) {
      toast.error(
        'Checkout failed',
        e instanceof Error ? e.message : 'Could not place the order.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-md px-6 py-16 text-center">
        <h1 className="text-xl font-bold">Sign in to check out</h1>
        <p className="mt-2 text-sm text-content-secondary">
          We’ll bring you back here as soon as you’re signed in.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="mb-6 text-xl font-bold tracking-tight sm:text-2xl">Checkout</h1>

      {bootstrapError && (
        <Alert variant="danger" className="mb-4">
          {bootstrapError}
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="grid gap-4">
          {/* Shipping address — collapsible. The header summarises
              the selected city/state when collapsed so the user
              doesn't need to expand to remember what they entered.
              `overflow-visible` (was `overflow-hidden`) so the
              City / State / Country SearchableSelect popovers don't
              get clipped by the Card boundary — bug ref AI Agent
              2026-05-26 "State dropdown not appearing properly". The
              collapse button is the first child and still gets rounded
              corners from the Card's own `rounded-lg`. */}
          <Card className="p-0 overflow-visible">
            <button
              type="button"
              onClick={() => setAddressOpen((o) => !o)}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-surface-card-hover"
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <div className="text-sm font-semibold">Shipping address</div>
                {!addressOpen && address.fullName && (
                  <span className="ml-2 truncate text-xs text-content-muted">
                    {address.fullName} ·{' '}
                    {[address.city, address.state, address.pin]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </span>
                )}
              </div>
              <ChevronDown
                className={`h-4 w-4 text-content-muted transition-transform ${
                  addressOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {addressOpen && (
              <div className="border-t border-border p-4">
                {/* Saved-address picker — shown only when the
                    customer has at least one entry in their address
                    book. Tapping a chip prefills the form. The
                    "Add new" chip clears the form so the customer
                    can enter a fresh one without touching any other
                    field. */}
                {savedAddresses.length > 0 && (
                  <div className="mb-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-xs font-medium text-content-secondary">
                        Use a saved address
                      </div>
                      <Link
                        to="/account/addresses"
                        className="text-[11px] font-medium text-primary hover:underline"
                      >
                        Manage
                      </Link>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {savedAddresses.map((a) => {
                        const active = selectedAddrId === a.id;
                        const summary = [a.city, a.pincode].filter(Boolean).join(' · ');
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => applySaved(a)}
                            className={`flex max-w-xs flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                              active
                                ? 'border-primary bg-primary-light text-primary'
                                : 'border-border bg-surface-card hover:bg-surface-card-hover'
                            }`}
                          >
                            <span className="flex items-center gap-1 font-semibold">
                              {a.label}
                              {a.isDefault && (
                                <span
                                  className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                    active
                                      ? 'bg-white/30 text-primary'
                                      : 'bg-primary/10 text-primary'
                                  }`}
                                >
                                  Default
                                </span>
                              )}
                            </span>
                            {summary && (
                              <span
                                className={`truncate ${
                                  active ? 'text-primary' : 'text-content-muted'
                                }`}
                              >
                                {summary}
                              </span>
                            )}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedAddrId('');
                          setAddress(EMPTY_ADDRESS);
                        }}
                        className={`flex items-center gap-1 rounded-md border px-3 py-2 text-xs transition-colors ${
                          selectedAddrId === ''
                            ? 'border-primary bg-primary-light text-primary'
                            : 'border-border bg-surface-card hover:bg-surface-card-hover'
                        }`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        New address
                      </button>
                    </div>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label="Full name"
                    value={address.fullName}
                    onChange={(e) => set('fullName', e.target.value)}
                    onBlur={markAddr}
                    error={addrErr("fullName")}
                    autoComplete="name"
                  />
                  <Input
                    label="Phone"
                    value={address.phone}
                    /* Strip non-digits at the input boundary — pasting a
                       formatted number ("+91 98765-43210") still works,
                       it just normalises to "919876543210". Using a
                       sanitiser is more forgiving than `type="tel"` +
                       `pattern`, which only validates on form submit and
                       still lets the user type letters silently.
                       Bug ref: AI Agent Issue #2. */
                    onChange={(e) =>
                      set('phone', e.target.value.replace(/\D+/g, '').slice(0, 15))
                    }
                    inputMode="numeric"
                    pattern="\d{10,15}"
                    maxLength={15}
                    placeholder="10-digit mobile"
                    autoComplete="tel"
                    onBlur={markAddr}
                    error={addrErr("phone")}
                  />
                  <Input
                    label="Address line 1"
                    value={address.line1}
                    onChange={(e) => set('line1', e.target.value)}
                    onBlur={markAddr}
                    error={addrErr("line1")}
                    placeholder="Street, area, landmark"
                    autoComplete="address-line1"
                    className="sm:col-span-2"
                  />
                  <Input
                    label="Address line 2"
                    value={address.line2}
                    onChange={(e) => set('line2', e.target.value)}
                    onBlur={markAddr}
                    error={addrErr("line2")}
                    placeholder="Building, floor (optional)"
                    autoComplete="address-line2"
                    className="sm:col-span-2"
                  />
                  <label className="flex flex-col gap-1">
                    <span className="flex items-center gap-1 text-xs font-medium text-content-secondary">
                      PIN code
                      {resolvingPin && (
                        <Loader2 className="h-3 w-3 animate-spin text-content-muted" />
                      )}
                    </span>
                    <input
                      inputMode="numeric"
                      pattern="\d*"
                      value={address.pin}
                      onChange={(e) =>
                        set(
                          'pin',
                          e.target.value.replace(/\D/g, '').slice(0, 6),
                        )
                      }
                      onBlur={markAddr}
                      placeholder="400050"
                      maxLength={6}
                      autoComplete="postal-code"
                      className={`h-10 w-full rounded-md border bg-surface-card px-3 font-mono text-sm text-content-primary outline-none focus:border-primary ${
                        addrErr('pin') ? 'border-danger' : 'border-border'
                      }`}
                    />
                    {addrErr('pin') && (
                      <span className="text-xs text-danger">{addrErr('pin')}</span>
                    )}
                  </label>
                  <SearchableSelect
                    label="City"
                    value={address.city}
                    onChange={(v) => set('city', v)}
                    options={INDIAN_CITIES}
                    placeholder="Pick or type a city"
                    searchPlaceholder="Search city…"
                    allowCustom
                  />
                  <SearchableSelect
                    label="State"
                    value={address.state}
                    onChange={(v) => set('state', v)}
                    options={INDIAN_STATES}
                    placeholder="Pick a state"
                    searchPlaceholder="Search state…"
                    allowCustom
                  />
                  <SearchableSelect
                    label="Country"
                    value={address.country}
                    onChange={(v) => set('country', v)}
                    options={COUNTRIES}
                    placeholder="Pick a country"
                    searchPlaceholder="Search country…"
                    allowCustom
                  />
                </div>
                <p className="mt-3 text-[11px] text-content-muted">
                  Tip: enter your 6-digit PIN code first — we’ll auto-fill
                  city, state and country.
                </p>
              </div>
            )}
          </Card>

          {/* Payment method — collapsible. Summary line shows the
              currently-selected method so collapsed mode still
              communicates intent. */}
          <Card className="p-0 overflow-hidden">
            <button
              type="button"
              onClick={() => setPaymentOpen((o) => !o)}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-surface-card-hover"
            >
              <div className="flex items-center gap-2">
                <Banknote className="h-4 w-4 text-primary" />
                <div className="text-sm font-semibold">Payment method</div>
                {!paymentOpen && (
                  <span className="ml-2 truncate text-xs text-content-muted">
                    {PAYMENT_METHODS.find((m) => m.id === payMode)?.label}
                  </span>
                )}
              </div>
              <ChevronDown
                className={`h-4 w-4 text-content-muted transition-transform ${
                  paymentOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {paymentOpen && (
              <div className="border-t border-border p-4">
                <div className="grid gap-2 sm:grid-cols-2">
                  {PAYMENT_METHODS.map(({ id, label, caption, Icon }) => {
                    const active = payMode === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setPayMode(id)}
                        className={`flex items-start gap-3 rounded-md border p-3 text-left text-sm transition-colors ${
                          active
                            ? 'border-primary bg-primary-light text-primary'
                            : 'border-border bg-surface-card hover:bg-surface-card-hover'
                        }`}
                      >
                        <Icon className="mt-0.5 h-5 w-5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{label}</div>
                          <div
                            className={`truncate text-[11px] ${
                              active ? 'text-primary' : 'text-content-muted'
                            }`}
                          >
                            {caption}
                          </div>
                        </div>
                        {active && <CheckCircle2 className="h-4 w-4 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-content-muted">
                  UPI / Card / Net banking / Wallet currently simulate a
                  successful capture — real gateway redirect ships once
                  the backend Payment-intent endpoint is wired to a
                  provider. COD records a Pending payment.
                </p>
              </div>
            )}
          </Card>
        </div>

        <aside className="h-fit rounded-lg border border-border bg-surface-card p-4">
          <div className="text-sm font-semibold">Order summary</div>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-content-secondary">
                Subtotal ({cart.count} items)
              </span>
              <span>₹{cart.subtotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between">
              {/* Label the rate only when every line shares one — a
                  mixed-slab cart has no single percentage to quote, and
                  the old hardcoded "18%" misdescribed any cart holding a
                  5% or 12% item. */}
              <span className="text-content-secondary">
                {gstRateLabel ? `Tax (${gstRateLabel}% GST)` : 'Tax (GST)'}
              </span>
              <span>₹{taxAmount.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-content-secondary">Shipping</span>
              <span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
            </div>
            <div className="my-2 border-t border-border" />
            <div className="flex justify-between text-base font-bold">
              <span>Total</span>
              <span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <Button
            fullWidth
            className="mt-4"
            onClick={placeOrder}
            disabled={!valid || submitting || bootstrapping || !!bootstrapError}
          >
            {submitting ? 'Placing order…' : `Pay ₹${total.toLocaleString('en-IN')}`}
          </Button>
          {!valid && (
            <p className="mt-2 text-[11px] text-content-muted">
              Fill in name, phone (10+ digits), address, city, state, PIN (6+ digits).
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
